import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BloqueOcupado } from '../services/CalendarService';
import type { EquipoCita, EquipoResumen } from '../services/TeamCalendarService';

// ─── Tipos del Dominio Logístico ──────────────────────────────────────────────

export type EstadoCita =
  | 'reservada_ttl'
  | 'pendiente_verificacion_pago'
  | 'en_revision'
  | 'confirmada'
  | 'pago_parcial'
  | 'rechazada'
  | 'expirada'
  | 'cancelada_paciente'
  | 'en_disputa'
  | 'en_sala';  // Paciente físicamente en el consultorio (estado visual local)

export interface CitaPendiente {
  cita_id: string;
  paciente_nombre: string;
  paciente_uuid: string;
  profesional_nombre: string;
  profesional_uuid: string;
  fecha_hora_inicio: string;
  fecha_hora_fin: string;
  estado: EstadoCita;
  servicio_titulo: string | null;
  // Comprobante
  comprobante_id: number | null;
  banco_emisor: string | null;
  referencia_6_digitos: string | null;
  telefono_emisor: string | null;
  monto_pagado: number | null;
  status_pago: string | null;
}

// ─── Tipo Calendario (solo datos logísticos, sin PHI clínica) ─────────────────

export interface CitaCalendario {
  cita_id: string;
  paciente_nombre: string;
  profesional_uuid: string;
  profesional_nombre: string;
  fecha_hora_inicio: string; // ISO 8601
  fecha_hora_fin: string;    // ISO 8601
  estado: EstadoCita | (string & {}); // Para soportar 'ocupado' o estados de eventos
  servicio_titulo: string | null;
  tipo: 'cita_digital' | 'cita_manual' | 'ocupado';
  nota?: string;
}

export interface ConfigDia {
  id: string;
  activo: boolean;
  intervalos: { inicio: string; fin: string }[];
}

export interface AgendaConfig {
  duracion_minutos: number;
  brecha_minutos: number;
  dias: Record<string, ConfigDia>;
}

// ─── Estado de la Pausa Activa (Nota 4: Ergonomía de Estrés) ─────────────────

export interface PausaActiva {
  citaId: string;
  citaNombre: string;
  isFromCalendar: boolean;
  timestamp: number;
}

export interface NotificacionUI {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning';
}

// ─── Limbo de Deshacer (7 segundos antes de disparar la RPC) ─────────────────

export interface UndoLimbo {
  message: string;
  timerId: ReturnType<typeof setTimeout> | null;
  expiresAt: number;
  onUndo: () => void;
  onCommit: () => Promise<void> | void;
}

// ─── Estado Consolidado (Nota 16.2: Anti-Fragmentación) ──────────────────────

interface SecretaryState {
  // Contexto de Workspace
  activeWorkspaceId: string | null;
  activeWorkspaceType: 'personal' | 'equipo';
  activeDoctorId: string | null;

  // ── Módulo: Cola de Pagos ─────────────────────────────────────────────────
  citasPendientes: CitaPendiente[];
  citaSeleccionada: CitaPendiente | null;
  isLoadingCitas: boolean;
  searchQuery: string;

  // ── Módulo: Calendario ───────────────────────────────────────────────────
  selectedDate: string;              // 'YYYY-MM-DD'
  citasCalendario: CitaCalendario[];
  configProfesionales: Record<string, AgendaConfig>;
  workspaceProfesionales: { uuid: string; nombre: string }[];
  isLoadingCalendario: boolean;
  citaCalendarioSeleccionada: CitaCalendario | null;
  isNuevaCitaOpen: boolean;
  isCheckoutCitaOpen: boolean;
  showCancelledCitas: boolean;

  // ── Buscador Inteligente (Mapa de Calor) ──────────────────────────────────
  isBuscadorInteligenteOpen: boolean;
  mesBuscadorBloques: BloqueOcupado[];
  selectedTimeSlot: { inicio: string, fin: string } | null;

  // ── Sistema de Pausa Activa ───────────────────────────────────────────────
  pausaActiva: PausaActiva | null;

  // ── Limbo de Deshacer (7s buffer) ────────────────────────────────────────
  undoLimbo: UndoLimbo | null;

  // ── Estado: Notificaciones y Migas de Pan ────────────────────────────────
  lastActionText: string | null;
  notificaciones: NotificacionUI[];

  // ── Acciones: Workspace ──────────────────────────────────────────────────
  setActiveWorkspace: (id: string, type: 'personal' | 'equipo') => void;
  setActiveDoctor: (id: string | null) => void;

  // ── Acciones: Cola de Pagos ───────────────────────────────────────────────
  setSearchQuery: (query: string) => void;
  setCitasPendientes: (citas: CitaPendiente[]) => void;
  setIsLoadingCitas: (loading: boolean) => void;
  seleccionarCita: (cita: CitaPendiente | null) => void;

  // ── Acciones: Calendario ─────────────────────────────────────────────────
  setSelectedDate: (date: string) => void;
  setCitasCalendario: (citas: CitaCalendario[]) => void;
  setConfigProfesionales: (configs: Record<string, AgendaConfig>) => void;
  setWorkspaceProfesionales: (wp: { uuid: string; nombre: string }[]) => void;
  setIsLoadingCalendario: (loading: boolean) => void;
  seleccionarCitaCalendario: (cita: CitaCalendario | null) => void;
  setNuevaCitaOpen: (isOpen: boolean) => void;
  setCheckoutCitaOpen: (isOpen: boolean) => void;
  setShowCancelledCitas: (show: boolean) => void;
  refreshCalendarioKey: number;
  forceRefreshCalendario: () => void;
  
  // ── Acciones: Buscador Inteligente ───────────────────────────────────────
  setBuscadorInteligenteOpen: (isOpen: boolean) => void;
  setMesBuscadorBloques: (bloques: BloqueOcupado[]) => void;
  setSelectedTimeSlot: (slot: { inicio: string, fin: string } | null) => void;

  // ── Acciones: Pausa Activa ────────────────────────────────────────────────
  activarPausa: (citaId: string, citaNombre: string, isFromCalendar: boolean) => void;
  reanudarPausa: () => void;

  // ── Acciones: Limbo de Deshacer ───────────────────────────────────────────
  iniciarLimbo: (message: string, onCommit: () => Promise<void>, onUndo: () => void) => void;
  deshacerLimbo: () => void;
  limpiarLimbo: () => void;

  // ── Acciones: Notificaciones y Migas de Pan ──────────────────────────────
  setLastActionText: (msg: string) => void;
  addNotificacion: (msg: string, type: 'info' | 'success' | 'warning') => void;
  removeNotificacion: (id: string) => void;

  // ── Módulo: Equipos (Escenario 2) ────────────────────────────────────────
  misEquipos: EquipoResumen[];
  activeEquipoId: string | null;
  citasEquipo: EquipoCita[];
  isLoadingEquipo: boolean;

  // ── Acciones: Equipos ────────────────────────────────────────────────────
  setMisEquipos: (equipos: EquipoResumen[]) => void;
  setActiveEquipoId: (id: string | null) => void;
  setCitasEquipo: (citas: EquipoCita[]) => void;
  setIsLoadingEquipo: (loading: boolean) => void;
  isNuevaCitaEquipoOpen: boolean;
  setNuevaCitaEquipoOpen: (isOpen: boolean) => void;

}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTodayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ─── Store Único ──────────────────────────────────────────────────────────────

export const useSecretaryStore = create<SecretaryState>()(
  persist(
    (set, get) => ({
  // Workspace
  activeWorkspaceId: null,
  activeWorkspaceType: 'personal',
  activeDoctorId: null,

  // Cola de pagos
  citasPendientes: [],
  citaSeleccionada: null,
  isLoadingCitas: false,
  searchQuery: '',

  // Calendario
  selectedDate: getTodayIso(),
  citasCalendario: [],
  configProfesionales: {},
  workspaceProfesionales: [],
  isLoadingCalendario: false,
  citaCalendarioSeleccionada: null,
  isNuevaCitaOpen: false,
  isCheckoutCitaOpen: false,
  showCancelledCitas: false,
  refreshCalendarioKey: 0,

  // Buscador Inteligente
  isBuscadorInteligenteOpen: false,
  mesBuscadorBloques: [],
  selectedTimeSlot: null,

  // Pausa / Limbo
  pausaActiva: null,
  undoLimbo: null,

  // Notificaciones y Migas de Pan
  lastActionText: null,
  notificaciones: [],

  // Equipos Clínicos (Escenario 2)
  misEquipos: [],
  activeEquipoId: null,
  citasEquipo: [],
  isLoadingEquipo: false,
  isNuevaCitaEquipoOpen: false,

  // ── Acciones: Workspace ─────────────────────────────────────────────────
  setActiveWorkspace: (id, type) => set({ activeWorkspaceId: id, activeWorkspaceType: type, activeDoctorId: null }),
  setActiveDoctor: (id) => set({ activeDoctorId: id }),

  // ── Acciones: Cola de Pagos ─────────────────────────────────────────────
  setSearchQuery: (query) => set({ searchQuery: query }),
  setCitasPendientes: (citas) => set({ citasPendientes: citas }),
  setIsLoadingCitas: (loading) => set({ isLoadingCitas: loading }),
  seleccionarCita: (cita) => set({ citaSeleccionada: cita }),

  // ── Acciones: Calendario ────────────────────────────────────────────────
  setSelectedDate: (date) => set({ selectedDate: date, citaCalendarioSeleccionada: null }),
  setCitasCalendario: (citas) => set({ citasCalendario: citas }),
  setConfigProfesionales: (configs) => set({ configProfesionales: configs }),
  setWorkspaceProfesionales: (wp) => set({ workspaceProfesionales: wp }),
  setIsLoadingCalendario: (loading) => set({ isLoadingCalendario: loading }),
  seleccionarCitaCalendario: (cita) => set({ citaCalendarioSeleccionada: cita }),
  setNuevaCitaOpen: (isOpen) => set({ isNuevaCitaOpen: isOpen }),
  setCheckoutCitaOpen: (isOpen) => set({ isCheckoutCitaOpen: isOpen }),
  setShowCancelledCitas: (show) => set({ showCancelledCitas: show }),
  forceRefreshCalendario: () => set((state) => ({ refreshCalendarioKey: state.refreshCalendarioKey + 1 })),
  
  // ── Acciones: Buscador Inteligente ──────────────────────────────────────
  setBuscadorInteligenteOpen: (isOpen) => set({ isBuscadorInteligenteOpen: isOpen }),
  setMesBuscadorBloques: (bloques) => set({ mesBuscadorBloques: bloques }),
  setSelectedTimeSlot: (slot) => set({ selectedTimeSlot: slot }),

  // ── Pausa Activa ────────────────────────────────────────────────────────
  activarPausa: (citaId, citaNombre, isFromCalendar) => {
    set({
      pausaActiva: { citaId, citaNombre, isFromCalendar, timestamp: Date.now() },
      citaSeleccionada: null,
      citaCalendarioSeleccionada: null,
    });
  },
  reanudarPausa: () => {
    const { pausaActiva, citasPendientes, citasCalendario } = get();
    if (!pausaActiva) return;
    
    if (pausaActiva.isFromCalendar) {
      const cita = citasCalendario.find((c) => c.cita_id === pausaActiva.citaId) ?? null;
      set({ pausaActiva: null, citaCalendarioSeleccionada: cita });
    } else {
      const cita = citasPendientes.find((c) => c.cita_id === pausaActiva.citaId) ?? null;
      set({ pausaActiva: null, citaSeleccionada: cita });
    }
  },

  // ── Limbo de Deshacer ───────────────────────────────────────────────────
  iniciarLimbo: (message, onCommit, onUndo) => {
    const prev = get().undoLimbo;
    if (prev?.timerId) {
      clearTimeout(prev.timerId);
      // BUG FIX: Si hay un Limbo activo y el usuario inicia otro, debemos 
      // forzar el commit del anterior para no perder la acción en la base de datos.
      if (prev.onCommit) prev.onCommit();
    }

    const timerId = setTimeout(() => {
      set({ undoLimbo: null });
      onCommit();
    }, 4000);

    set({
      undoLimbo: {
        message,
        timerId,
        expiresAt: Date.now() + 4000,
        onUndo,
        onCommit,
      },
    });
  },
  deshacerLimbo: () => {
    const { undoLimbo } = get();
    if (undoLimbo) {
      if (undoLimbo.timerId) clearTimeout(undoLimbo.timerId);
      undoLimbo.onUndo();
      set({ undoLimbo: null });
    }
  },
  limpiarLimbo: () => {
    set({ undoLimbo: null });
  },

  // ── Notificaciones y Migas de Pan ────────────────────────────────────────
  setLastActionText: (msg) => set({ lastActionText: msg }),
  
  addNotificacion: (msg, type) => {
    const id = crypto.randomUUID();
    set((state) => ({
      notificaciones: [...state.notificaciones, { id, message: msg, type }]
    }));
    // Auto remove after 5 seconds
    setTimeout(() => {
      get().removeNotificacion(id);
    }, 5000);
  },
  
  removeNotificacion: (id) => {
    set((state) => ({
      notificaciones: state.notificaciones.filter((n) => n.id !== id)
    }));
  },

  // ── Equipos Clínicos (Escenario 2) ─────────────────────────────────
  setMisEquipos: (equipos) => set({ misEquipos: equipos }),
  setActiveEquipoId: (id) => set({ activeEquipoId: id }),
  setCitasEquipo: (citas) => set({ citasEquipo: citas }),
  setIsLoadingEquipo: (loading) => set({ isLoadingEquipo: loading }),
  setNuevaCitaEquipoOpen: (isOpen) => set({ isNuevaCitaEquipoOpen: isOpen }),

    }),
    {
      name: 'secretary-storage',
      partialize: (state) => ({
        activeWorkspaceId: state.activeWorkspaceId,
        activeDoctorId: state.activeDoctorId,
        activeWorkspaceType: state.activeWorkspaceType,
      }),
    }
  )
);
